import type { ElementType } from "react"
import { AlertTriangle, Plus, Settings } from "lucide-react"
import type { SuggestedRailId } from "@/lib/sidebar-rail-suggested"
import { ShellPanelToggle } from "@/components/layout/shell"
import { useShellChrome } from "./resizable-layout.flyout"
import { testIds } from "@/lib/test-ids"
import { cn } from "@/lib/utils"
import { SidebarWorkspaceScopeButton } from "./sidebar-workspace-scope-button"
import {
  SHELL_COLUMN_FOOTER_CLASS,
  SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
  SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
  SHELL_ICON_RAIL_BUTTON_DIMMED_CLASS,
  SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
  SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
  SHELL_ICON_RAIL_BUTTON_SUGGESTED_PULSE_CLASS,
  SHELL_ICON_RAIL_CLASS,
} from "@/lib/shell-chrome"

export type RailItem = {
  id: 'browse' | 'inbox' | 'flows' | 'chats' | 'drive' | 'basecamp' | 'nextcloud'
  Icon: ElementType
  title: string
  badge: string | null
  onClick: () => void
  testId?: string
}

type RailStateProps = {
  primaryMode: RailItem['id'] | null
  suggestedRailId: SuggestedRailId | null
  driveConnected: boolean
  basecampDisconnected: boolean
}

export function LeftSidebarRailButton({
  id,
  Icon,
  title,
  onClick,
  badge = null,
  testId,
  primaryMode,
  suggestedRailId,
  driveConnected,
  basecampDisconnected,
}: RailItem & RailStateProps) {
  const active = primaryMode === id
  const suggested = !active && suggestedRailId === id
  const driveDimmed = id === 'drive' && !driveConnected && !active
  const label = id === 'basecamp' && basecampDisconnected
    ? `${title}, disconnected`
    : suggested ? `Show ${title}` : title
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      data-suggested={suggested ? 'true' : undefined}
      data-testid={testId}
      className={cn(
        'group',
        SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
        active
          ? SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS
          : suggested
            ? cn(
                SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
                SHELL_ICON_RAIL_BUTTON_SUGGESTED_PULSE_CLASS,
              )
            : driveDimmed
              ? SHELL_ICON_RAIL_BUTTON_DIMMED_CLASS
              : SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4',
          id === 'basecamp' &&
            'transition-[filter] duration-150',
          id === 'basecamp' &&
            !active &&
            !suggested &&
            'grayscale group-hover:grayscale-0',
        )}
      />
      {id === 'basecamp' && basecampDisconnected ? (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-400 text-amber-950"
          title="Basecamp is disconnected"
          data-testid={testIds.basecampSidebar.railAlert}
        >
          <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
        </span>
      ) : badge ? (
        <span className="absolute -right-0.5 -top-0.5 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-primary px-0.5 text-[8px] font-bold leading-none text-primary-foreground">
          {badge}
        </span>
      ) : null}
    </button>
  )
}

export function LeftSidebarIconRail({
  collapsed,
  onToggleCollapse,
  coreRailItems,
  extensionRailItems,
  primaryMode,
  suggestedRailId,
  driveConnected,
  basecampDisconnected,
  onAddExtension,
  onOpenSettings,
}: RailStateProps & {
  collapsed: boolean
  onToggleCollapse: () => void
  coreRailItems: RailItem[]
  extensionRailItems: RailItem[]
  onAddExtension: () => void
  onOpenSettings: () => void
}) {
  const { leftDisabledReason, autoHide } = useShellChrome()
  const railState: RailStateProps = {
    primaryMode,
    suggestedRailId,
    driveConnected,
    basecampDisconnected,
  }
  return (
    <div
      className={cn(
        SHELL_ICON_RAIL_CLASS,
        "group/icon-rail bg-sidebar-accent border-r border-border flex flex-col flex-none h-full",
      )}
      data-testid={testIds.leftSidebar.iconBar}
    >
      <ShellPanelToggle
        side="left"
        placement="slot"
        pinMode={autoHide}
        open={!collapsed}
        disabledReason={collapsed ? leftDisabledReason : undefined}
        onToggle={onToggleCollapse}
      />
      <SidebarWorkspaceScopeButton />

      <div className="flex flex-col items-center gap-1 pt-1">
        <div
          className="flex flex-col items-center gap-1"
          data-testid={testIds.leftSidebar.iconBarCore}
        >
          {coreRailItems.map((item) => (
            <LeftSidebarRailButton key={item.id} {...item} {...railState} />
          ))}
        </div>
        <div className="my-1.5 h-px w-6 shrink-0 bg-border" aria-hidden />
        <div
          className="flex flex-col items-center gap-1"
          data-testid={testIds.leftSidebar.iconBarExtensions}
        >
          {extensionRailItems.map((item) => (
            <LeftSidebarRailButton key={item.id} {...item} {...railState} />
          ))}
          <button
            type="button"
            title="Add extension"
            aria-label="Add extension"
            data-testid={testIds.leftSidebar.railAddExtension}
            onClick={onAddExtension}
            className={cn(
              SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
              SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
              'opacity-40 duration-200 ease-in-out',
              'transition-[opacity,color,background-color,box-shadow,transform]',
              'group-hover/sidebar:opacity-100',
              'focus-visible:opacity-100',
            )}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onOpenSettings}
        title="Settings"
        aria-label="Settings"
        data-testid={testIds.leftSidebar.settingsIcon}
        className={cn(
          SHELL_ICON_RAIL_CLASS,
          SHELL_COLUMN_FOOTER_CLASS,
          'flex items-center justify-center border-t border-border',
          'text-sidebar-foreground/45 transition-[color,background-color] duration-150',
          'hover:bg-primary/10 hover:text-sidebar-foreground',
        )}
      >
        <Settings className="w-4 h-4" />
      </button>
    </div>
  )
}
